import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image, Platform } from 'react-native';
import { colores, fuentes } from '../../../styles/variablesCss';
import Storage from '../../../utils/Storage';
import { BACKEND_URL } from '../../../Config/Const';
import AddCarrito from './Carrito/AddCarrito';
import { formatPrecio } from '../../../utils/formatPrice';

const getMinPurchaseQuantity = (producto) => {
  const min = Number(producto?.minStock);
  return Number.isFinite(min) && min > 0 ? min : 1;
};

const getUnitBasePrice = (producto) => {
  const precio = Number(producto?.precio);
  return Number.isFinite(precio) && precio > 0 ? precio : 0;
};

const DesplegableProductos = ({ searchQuery, isFocused, onSelect }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [visible, setVisible] = useState(false);
  const [cantidades, setCantidades] = useState({});
  const initialWidth = Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : 1024;
  const [viewportWidth, setViewportWidth] = useState(initialWidth);
  const isSmallWeb = Platform.OS === 'web' && viewportWidth < 480;

  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const abortRef = useRef(null);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const onResize = () => setViewportWidth(window.innerWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
  }, []);

  useEffect(() => {
    const q = (searchQuery || '').trim();

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (e) {
      }
      abortRef.current = null;
    }

    if (q.length < 3) {
      requestIdRef.current++;
      setSuggestions([]);
      setVisible(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        abortRef.current = controller;

        const suc = (await Storage.getItem('sucursalId')) || '4';
        const params = new URLSearchParams({ suc: String(suc), producto: q });
        const response = await fetch(`${BACKEND_URL}/rest/productos/busqueda?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          ...(controller ? { signal: controller.signal } : {}),
        });

        const data = await response.json().catch(() => ({}));
        if (requestId !== requestIdRef.current) return;

        const productosBusqueda =
          data?.productos ||
          data?.dsResultado?.productos ||
          data?.dsResultado?.Productos ||
          [];

        const list = Array.isArray(productosBusqueda) ? productosBusqueda : [];
        const top = list
          .slice()
          .sort((a, b) => (Number(b?.cantVentas || 0) - Number(a?.cantVentas || 0)))
          .slice(0, 4);

        setSuggestions(top);
        setCantidades((prev) => {
          const next = { ...prev };
          for (const p of top) {
            const plu = String(p?.plu);
            if (!plu) continue;
            if (typeof next[plu] === 'undefined') {
              const min = p?.minStock ? Number(p.minStock) : 1;
              next[plu] = Number.isFinite(min) && min > 0 ? min : 1;
            }
          }
          return next;
        });
        setVisible(true);
      } catch (e) {
      }
    }, 250);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch (e) {
        }
        abortRef.current = null;
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    if (!isFocused) {
      setTimeout(() => setVisible(false), 120);
      return;
    }

    const q = (searchQuery || '').trim();
    if (q.length >= 3 && suggestions && suggestions.length > 0) {
      setVisible(true);
    }
  }, [isFocused, searchQuery, suggestions]);

  if (!isFocused || !visible || !suggestions || suggestions.length === 0) return null;

  const getMaxAllowed = (p) => {
    const maxStock = Number(p?.maxStock || 0);
    if (Number.isFinite(maxStock) && maxStock > 0) return maxStock;
    const stock = Number(p?.stock || 0);
    if (Number.isFinite(stock) && stock > 0) return stock;
    return Infinity;
  };

  const inc = (p) => {
    const plu = String(p?.plu);
    if (!plu) return;
    const step = p?.minStock ? Number(p.minStock) : 1;
    const incBy = Number.isFinite(step) && step > 0 ? step : 1;
    const max = getMaxAllowed(p);
    setCantidades((prev) => {
      const cur = Number(prev[plu] ?? incBy);
      const nextVal = cur + incBy;
      return { ...prev, [plu]: max !== Infinity ? Math.min(nextVal, max) : nextVal };
    });
  };

  const dec = (p) => {
    const plu = String(p?.plu);
    if (!plu) return;
    const step = p?.minStock ? Number(p.minStock) : 1;
    const decBy = Number.isFinite(step) && step > 0 ? step : 1;
    setCantidades((prev) => {
      const cur = Number(prev[plu] ?? decBy);
      const nextVal = cur - decBy;
      return { ...prev, [plu]: Math.max(nextVal, decBy) };
    });
  };

  const dropdownWidth = isSmallWeb ? Math.min(360, Math.max(280, viewportWidth - 50)) : null;

  return (
    <View
      style={[
        styles.searchSuggestionsDropdown,
        isSmallWeb && dropdownWidth
          ? {
              width: dropdownWidth,
              left: '40%',
              right: undefined,
              transform: [{ translateX: -dropdownWidth / 2 }],
            }
          : null,
      ]}
    >
      {suggestions.map((p) => {
        const ofertaCode = p?.oferta != null ? String(p.oferta).trim() : '';
        const hasOferta = ofertaCode === 'N' || ofertaCode === 'D' || (!!ofertaCode && ofertaCode !== '0');
        const comboArr = Array.isArray(p?.combo) ? p.combo : [];
        const hasCombo = (comboArr && comboArr.length > 0) || (Number(p?.codCodeca || 0) !== 0);
        const combo0 = comboArr && comboArr.length > 0 ? comboArr[0] : null;
        const cancom = combo0?.cancom != null ? Number(combo0.cancom) : 0;

        const pluKey = String(p?.plu);
        const cantidad = Number(cantidades?.[pluKey] ?? (p?.minStock ? Number(p.minStock) : 1));
        const maxAllowed = getMaxAllowed(p);
        const disableInc = maxAllowed !== Infinity && cantidad >= maxAllowed;

        const comboTieneDatos = comboArr && comboArr.length > 0;
        const totalCantObligCombo = comboTieneDatos
          ? comboArr.reduce((sum, c) => sum + Number(c?.cantOblig || 0), 0)
          : 0;
        const esPluRegalo = combo0 && Number(combo0?.tipoCombo) === 3 && Number(p?.plu) === Number(combo0?.pluRegalo);
        const minPurchaseQuantity = getMinPurchaseQuantity(p);
        const unitBasePrice = getUnitBasePrice(p);
        const regularPriceToShow = unitBasePrice * minPurchaseQuantity;

        const comboBasePrice = (() => {
          if (!comboTieneDatos) return unitBasePrice;
          if (esPluRegalo) return unitBasePrice;
          if (!combo0) return unitBasePrice;
          if (Number(combo0?.tipoCombo) === 4 || totalCantObligCombo > 0) return unitBasePrice;
          const comboPrice = Number(combo0?.precom);
          return Number.isFinite(comboPrice) && comboPrice > 0 ? comboPrice : unitBasePrice;
        })();

        const comboPriceToShow = comboTieneDatos ? comboBasePrice * minPurchaseQuantity : NaN;
        const hasComboPrice =
          comboTieneDatos &&
          Number.isFinite(comboPriceToShow) &&
          comboPriceToShow > 0 &&
          (!Number.isFinite(regularPriceToShow) || Math.abs(comboPriceToShow - regularPriceToShow) > 0.01);

        return (
          <View key={String(p?.plu)} style={[styles.searchSuggestionCard, isSmallWeb && styles.searchSuggestionCardSmall]}>
            <TouchableOpacity
              style={[styles.searchSuggestionTapArea, isSmallWeb && styles.searchSuggestionTapAreaSmall]}
              activeOpacity={0.85}
              onPress={() => {
                setVisible(false);
                if (typeof onSelect === 'function') onSelect(p?.plu);
              }}
            >
              <Image source={{ uri: p?.foto }} style={styles.searchSuggestionImg} resizeMode="contain" />
              <View style={styles.searchSuggestionInfo}>
                <Text style={styles.searchSuggestionTitle} numberOfLines={1}>{p?.descripcion}</Text>
                <View style={styles.searchSuggestionMetaRow}>
                  {hasComboPrice ? (
                    <View style={{ minWidth: 0 }}>
                      {Number.isFinite(cancom) && cancom > 1 && (
                        <Text style={{ fontSize: 11, color: '#777' }}>Llevando {cancom} pagas c/u</Text>
                      )}
                      <Text style={styles.searchSuggestionPrice}>$ {formatPrecio(comboPriceToShow, true)}</Text>
                      {/* Mostrar precio tachado solo cuando oferta es N o D */}
                    </View>
                  ) : (
                    <View style={{ minWidth: 0 }}>
                      {Number.isFinite(cancom) && cancom > 1 && (
                        <Text style={{ fontSize: 11, color: '#777' }}>Llevando {cancom} pagas c/u</Text>
                      )}
                      <Text style={styles.searchSuggestionPrice}>$ {formatPrecio(regularPriceToShow, true)}</Text>
                    </View>
                  )}
                  <View style={styles.searchSuggestionBadges}>
                    {hasOferta && (
                      <View style={styles.searchSuggestionBadgeOferta}>
                        <Text style={styles.searchSuggestionBadgeText}>OFERTA</Text>
                      </View>
                    )}
                    {hasCombo && (
                      <View style={styles.searchSuggestionBadgeCombo}>
                        <Text style={styles.searchSuggestionBadgeText}>COMBO</Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>
            </TouchableOpacity>

            <View style={[styles.searchSuggestionActions, isSmallWeb && styles.searchSuggestionActionsSmall]}>
              <View style={styles.qtyControls}>
                <TouchableOpacity style={styles.qtyBtn} onPress={() => dec(p)}>
                  <Text style={styles.qtyBtnText}>-</Text>
                </TouchableOpacity>
                <View style={styles.qtyValue}>
                  <Text style={styles.qtyValueText} numberOfLines={1}>
                    {Number.isFinite(cantidad) ? (Number.isInteger(cantidad) ? String(cantidad) : cantidad.toFixed(2)) : String(cantidad)}
                  </Text>
                </View>
                <TouchableOpacity style={[styles.qtyBtn, disableInc && styles.qtyBtnDisabled]} onPress={() => inc(p)} disabled={disableInc}>
                  <Text style={[styles.qtyBtnText, disableInc && styles.qtyBtnTextDisabled]}>+</Text>
                </TouchableOpacity>
              </View>

              <View style={styles.addBtnWrapper}>
                <AddCarrito 
                  plu={p?.plu} 
                  cantidad={cantidad} 
                  showText={false} 
                  style={styles.addBtnCompact}
                  productName={p?.nombre || p?.descripcion}
                  productPrice={p?.precio}
                  productCategory={p?.desCat}
                />
              </View>
            </View>
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  searchSuggestionsDropdown: {
    position: 'absolute',
    top: 44,
    left: 0,
    right: 0,
    backgroundColor: colores.blanco,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    zIndex: 2000,
    overflow: 'visible',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 8px 20px rgba(0,0,0,0.18)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 6 },
          shadowOpacity: 0.18,
          shadowRadius: 10,
          elevation: 10,
        }
    ),
  },
  searchSuggestionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
    backgroundColor: colores.blanco,
  },
  searchSuggestionCardSmall: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
  },
  searchSuggestionTapArea: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchSuggestionTapAreaSmall: {
    width: '100%',
  },
  searchSuggestionImg: {
    width: 44,
    height: 44,
    marginRight: 10,
    backgroundColor: 'transparent',
  },
  searchSuggestionInfo: {
    flex: 1,
    minWidth: 0,
  },
  searchSuggestionTitle: {
    fontSize: 13,
    fontFamily: fuentes.medium,
    color: colores.textoPrincipal,
  },
  searchSuggestionMetaRow: {
    marginTop: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  searchSuggestionPrice: {
    fontSize: 13,
    fontFamily: fuentes.bold,
    color: colores.rojo,
  },
  searchSuggestionBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  searchSuggestionBadgeOferta: {
    backgroundColor: colores.rojo,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
  },
  searchSuggestionBadgeCombo: {
    backgroundColor: colores.rojo,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
  },
  searchSuggestionBadgeText: {
    color: colores.blanco,
    fontFamily: fuentes.bold,
    fontSize: 10,
    letterSpacing: 0.2,
  },
  searchSuggestionActions: {
    marginLeft: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchSuggestionActionsSmall: {
    width: '100%',
    marginLeft: -20,
    justifyContent: 'flex-end',
  },
  qtyControls: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E5E5E5',
    borderRadius: 8,
    overflow: 'hidden',
    height: 32,
  },
  qtyBtn: {
    width: 28,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F7F7F7',
  },
  qtyBtnDisabled: {
    opacity: 0.6,
  },
  qtyBtnText: {
    fontSize: 18,
    color: '#666',
  },
  qtyBtnTextDisabled: {
    color: '#AAA',
  },
  qtyValue: {
    width: 44,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colores.blanco,
  },
  qtyValueText: {
    fontSize: 13,
    fontFamily: fuentes.medium,
    color: colores.textoPrincipal,
  },
  addBtnWrapper: {
    width: 36,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addBtnCompact: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
});

export default DesplegableProductos;
